/**
 * Mobile site summary bar — always-visible identity + status header for the
 * single-site dashboard on compact screens.
 *
 * Sits under the global nav while the full context cards live in a slide-out
 * drawer. Gives an at-a-glance read of the site: name, an active-alarm count,
 * comm (online) status, the attached device, and location. The identity area
 * is the tap target that opens the drawer; an optional quick-action cluster
 * gives one-tap device commands (its taps stop propagation so they don't open
 * the drawer). A trailing chevron signals the row is tappable.
 *
 * Contains:
 * - MobileSiteSummaryBar — the summary header (default export)
 */
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RouterIcon from '@mui/icons-material/Router';
import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';
import CommandActivityIndicator from '@sophocles/views/sites/components/SiteDashboard/CommandActivityIndicator';

// Maps BasicDashboardCard status → MUI Chip color for the alarm count.
const ALARM_CHIP_COLOR = { warning: 'warning', bad: 'error', purple: 'secondary' };
const ALARM_STATUS_DOT = {
  warning: 'var(--status-warning)',
  bad: 'var(--status-bad)',
  purple: 'var(--status-attention)',
};

function MetaItem({ icon, children }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ minWidth: 0, overflow: 'hidden', color: 'text.secondary' }}
    >
      {icon}
      <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
        {children}
      </Typography>
    </Stack>
  );
}

MetaItem.propTypes = { icon: PropTypes.node, children: PropTypes.node };

function MobileSiteSummaryBar({
  name,
  location,
  deviceName,
  hasDevice,
  online,
  alarmCount,
  alarmStatus,
  commandActivity,
  deactivated,
  quickActions,
  phone = false,
  onOpenDetails,
}) {
  const { t } = useTranslation();
  const hasAlarms = alarmCount > 0;
  // online is true/false when a radio exists, null when there's no radio.
  const showComm = hasDevice && online !== null && online !== undefined;
  const statusDot = hasAlarms
    ? (ALARM_STATUS_DOT[alarmStatus] ?? 'var(--status-warning)')
    : showComm && online
      ? 'var(--status-ok)'
      : 'var(--status-neutral)';

  if (phone) {
    const detailsLabel = t('actions.showDetails', 'Show details');

    return (
      <Box sx={{ px: 'var(--page-px)', py: 'var(--space-2)' }}>
        <ButtonBase
          onClick={onOpenDetails}
          aria-label={`${detailsLabel}: ${name}`}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            px: 'var(--space-1)',
            py: 'var(--space-1)',
            borderRadius: 'var(--radius-md)',
            textAlign: 'left',
            '&:hover': { bgcolor: 'var(--site-mobile-bar-meta-bg)' },
            '&.Mui-focusVisible': { boxShadow: 'var(--shadow-focus)' },
          }}
        >
          <Box
            sx={{
              width: 'var(--space-8)',
              height: 'var(--space-8)',
              borderRadius: 'var(--radius-md)',
              bgcolor: 'var(--site-mobile-bar-meta-bg)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 'var(--space-2)',
                height: 'var(--space-2)',
                borderRadius: '50%',
                bgcolor: statusDot,
                boxShadow: `0 0 0 var(--space-1) color-mix(in srgb, ${statusDot} 14%, transparent)`,
              }}
            />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                noWrap
                sx={{ minWidth: 0, flex: 1, fontWeight: 'var(--weight-semibold)', lineHeight: 1.2 }}
              >
                {name}
              </Typography>
              {hasAlarms ? (
                <Chip
                  size="small"
                  color={ALARM_CHIP_COLOR[alarmStatus] || 'warning'}
                  icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={alarmCount}
                  sx={{ height: 20, fontWeight: 700, flexShrink: 0, '& .MuiChip-label': { px: 0.5 } }}
                />
              ) : null}
              {deactivated ? (
                <StatusChip color="neutral" label={t('general.deactivated')} sx={{ flexShrink: 0 }} />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
              {location || deviceName || t('sites.siteDetails', 'Site details')}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: 'primary.main', flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 'var(--weight-semibold)' }}>
              {t('general.details', 'Details')}
            </Typography>
            <ChevronRightIcon fontSize="small" />
          </Stack>
        </ButtonBase>

        <Box
          sx={{
            mt: 'var(--space-2)',
            minHeight: 'calc(var(--space-8) + var(--space-1))',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            px: 'var(--space-2)',
            py: 'var(--space-1)',
            bgcolor: 'var(--site-mobile-bar-meta-bg)',
            borderRadius: 'var(--button-radius)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <CommandActivityIndicator activity={commandActivity} />
            {showComm ? (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 'var(--space-2)',
                    height: 'var(--space-2)',
                    borderRadius: '50%',
                    bgcolor: online ? 'var(--status-ok)' : 'var(--status-neutral)',
                  }}
                />
                <Typography variant="caption" sx={{ color: online ? 'var(--status-ok-text)' : 'text.secondary' }}>
                  {online ? t('general.online', 'Online') : t('general.offline', 'Offline')}
                </Typography>
              </Stack>
            ) : null}
            {hasDevice ? <MetaItem icon={<RouterIcon sx={{ fontSize: 14 }} />}>{deviceName}</MetaItem> : null}
          </Stack>

          {quickActions ? (
            <Box
              sx={{
                flexShrink: 0,
                '& .MuiStack-root': { gap: 'var(--space-1)' },
                '& .MuiIconButton-root': {
                  width: 'var(--space-8)',
                  height: 'var(--space-8)',
                  bgcolor: 'var(--surface-primary)',
                  boxShadow: 'var(--card-shadow-raised)',
                  '&:hover': {
                    bgcolor: 'var(--surface-primary)',
                    boxShadow: 'var(--card-shadow-raised-hover)',
                  },
                },
              }}
            >
              {quickActions}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        px: 'var(--page-px)',
        py: 'var(--space-3)',
      }}
    >
      {/* Identity + status — the drawer tap target */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 'var(--weight-semibold)', lineHeight: 1.2 }}>
            {name}
          </Typography>
          {hasAlarms ? (
            <Chip
              size="small"
              color={ALARM_CHIP_COLOR[alarmStatus] || 'warning'}
              icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />}
              label={alarmCount}
              sx={{ height: 20, fontWeight: 700, flexShrink: 0, '& .MuiChip-label': { px: 0.5 } }}
            />
          ) : null}
          {deactivated ? <StatusChip color="neutral" label={t('general.deactivated')} sx={{ flexShrink: 0 }} /> : null}
          <CommandActivityIndicator activity={commandActivity} />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.25, flexWrap: 'wrap' }} useFlexGap>
          {showComm ? (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: online ? 'success.main' : 'text.disabled',
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ color: online ? 'success.main' : 'text.secondary' }}>
                {online ? t('general.online', 'Online') : t('general.offline', 'Offline')}
              </Typography>
            </Stack>
          ) : null}
          {hasDevice ? <MetaItem icon={<RouterIcon sx={{ fontSize: 14 }} />}>{deviceName}</MetaItem> : null}
          {location ? <MetaItem icon={<LocationOnOutlinedIcon sx={{ fontSize: 14 }} />}>{location}</MetaItem> : null}
        </Stack>
      </Box>

      {/* Quick actions — stop propagation so taps don't open the drawer */}
      {quickActions ? <Box sx={{ flexShrink: 0 }}>{quickActions}</Box> : null}

      <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </Box>
  );
}

MobileSiteSummaryBar.propTypes = {
  name: PropTypes.string,
  location: PropTypes.string,
  deviceName: PropTypes.string,
  hasDevice: PropTypes.bool,
  /** Radio online flag: true/false when a radio exists, null/undefined otherwise. */
  online: PropTypes.bool,
  /** Count of active (non-hidden) alarms. */
  alarmCount: PropTypes.number,
  /** BasicDashboardCard status string: 'good' | 'warning' | 'bad' | 'purple'. */
  alarmStatus: PropTypes.string,
  /** Server-side command queue state from the site read/socket contract. */
  commandActivity: PropTypes.shape({
    state: PropTypes.oneOf(['idle', 'queued', 'sending']),
    pending_count: PropTypes.number,
    pending_since: PropTypes.string,
  }),
  /** Site deactivated flag from the dashboard contract. */
  deactivated: PropTypes.bool,
  /** Optional quick-action cluster (e.g. SiteQuickActions) rendered on the right. */
  quickActions: PropTypes.node,
  /** Phone-only presentation; compact/tablet callers keep the existing summary row. */
  phone: PropTypes.bool,
  /** Opens the phone details bottom sheet. */
  onOpenDetails: PropTypes.func,
};

export default MobileSiteSummaryBar;
